import { post } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

interface Params {
  projectId: string;
  sensorId: number | string;
  targetInstallationId: number | string;
  currentInstallationId: number | string;
}

export function useMoveSensor() {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: ({ projectId, sensorId, targetInstallationId }: Params) =>
      post({
        url: `/projects/${projectId}/sensors/${sensorId}/move`,
        body: {
          installationId: Number(targetInstallationId),
        },
      }),
    onSuccess: (_, { projectId, currentInstallationId, targetInstallationId }) => {
      const invalidateInstallation = (installationId: number | string) => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.getGateways(projectId, installationId.toString()),
        });
      };

      invalidateInstallation(currentInstallationId);
      invalidateInstallation(targetInstallationId);

      queryClient.invalidateQueries({
        queryKey: queryKeys.getGateways(projectId, 'installationId'),
      });
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          if (!Array.isArray(queryKey) || queryKey[0] !== 'sensors') {
            return false;
          }

          const queryParams = queryKey[1] as { projectId?: string } | undefined;
          return queryParams?.projectId === projectId;
        },
      });
    },
  });

  const moveSensor = useCallback((params: Params) => mutate(params), [mutate]);

  return {
    moveSensor,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
